import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  CONSENT_EVENT,
  applyStoredConsent,
  getStoredConsent,
  isStrictConsentRegion,
  saveConsent,
  type ConsentCategories,
} from "@/lib/cookieConsent";
import { useLocalePath } from "@/hooks/useLocalePath";

const ALL_ON: ConsentCategories = { essential: true, analytics: true, marketing: true, functional: true };
const ALL_OFF: ConsentCategories = { essential: true, analytics: false, marketing: false, functional: false };
/** Implied-consent default outside the EEA/UK/CH: analytics on, marketing off. */
const IMPLIED: ConsentCategories = { essential: true, analytics: true, marketing: false, functional: true };

export const CookieConsentBanner: React.FC = () => {
  const { localePath } = useLocalePath();
  const [visible, setVisible] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [strict] = useState(() => isStrictConsentRegion());
  const [draft, setDraft] = useState<ConsentCategories>(ALL_OFF);

  const defaultDraft = strict ? ALL_OFF : IMPLIED;

  useEffect(() => {
    const stored = getStoredConsent();
    if (stored) {
      applyStoredConsent();
      setDraft({ essential: true, analytics: stored.analytics, marketing: stored.marketing, functional: stored.functional });
    } else {
      setDraft(defaultDraft);
      setVisible(true);
    }
  }, [defaultDraft]);


  useEffect(() => {
    const open = () => {
      const stored = getStoredConsent();
      setDraft(stored ? { essential: true, analytics: stored.analytics, marketing: stored.marketing, functional: stored.functional } : defaultDraft);
      setPrefsOpen(true);
    };
    window.addEventListener(CONSENT_EVENT, open);
    return () => window.removeEventListener(CONSENT_EVENT, open);
  }, [defaultDraft]);

  const commit = useCallback((consent: ConsentCategories) => {
    saveConsent(consent);
    setVisible(false);
    setPrefsOpen(false);
  }, []);

  return (
    <>
      {visible && (
        <div
          role="region"
          aria-label="Cookie consent"
          className="fixed inset-x-0 bottom-0 z-50 p-3 sm:p-4 pointer-events-none"
        >
          <div className="pointer-events-auto mx-auto max-w-5xl rounded-xl border border-border bg-card/95 backdrop-blur-md shadow-lg p-4 sm:p-5 flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex gap-3 flex-1">
              <Cookie className="h-5 w-5 text-primary shrink-0 mt-0.5" aria-hidden="true" />
              <p className="text-sm text-muted-foreground leading-relaxed">
                {strict ? (
                  <>
                    We use essential cookies to run this site, and — only with your consent — analytics and marketing
                    cookies to understand usage and measure campaigns.{" "}
                  </>
                ) : (
                  <>
                    We use essential and analytics cookies to run this site and understand how it is used. Marketing
                    cookies are only set if you accept them. You can opt out at any time.{" "}
                  </>
                )}
                Read our{" "}
                <Link to={localePath("/cookie-policy")} className="text-foreground underline underline-offset-4 hover:text-primary">
                  Cookie Policy
                </Link>
                .
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 md:shrink-0">
              <Button variant="ghost" size="sm" onClick={() => { setDraft(defaultDraft); setPrefsOpen(true); }}>
                Manage preferences
              </Button>
              <Button variant="outline" size="sm" onClick={() => commit(ALL_OFF)}>
                Reject non-essential
              </Button>
              <Button size="sm" onClick={() => commit(ALL_ON)}>
                Accept all
              </Button>
            </div>
          </div>
        </div>
      )}


      <Dialog open={prefsOpen} onOpenChange={setPrefsOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Cookie preferences</DialogTitle>
            <DialogDescription>
              Choose which cookies TrustScale may use. You can change this at any time from the Cookie Policy page.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <PrefRow
              title="Essential"
              description="Required for security, session state and storing your cookie choice. Always on."
              checked
              disabled
            />
            <PrefRow
              title="Functional"
              description="Remembers preferences such as your language and display settings."
              checked={draft.functional}
              onChange={(v) => setDraft((d) => ({ ...d, functional: v }))}
            />
            <PrefRow
              title="Analytics"
              description="Google Analytics, to understand how visitors use the site."
              checked={draft.analytics}
              onChange={(v) => setDraft((d) => ({ ...d, analytics: v }))}
            />
            <PrefRow
              title="Marketing"
              description="Google Ads and DoubleClick, for conversion measurement and ad relevance."
              checked={draft.marketing}
              onChange={(v) => setDraft((d) => ({ ...d, marketing: v }))}
            />
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" size="sm" onClick={() => commit(ALL_OFF)}>
              Reject non-essential
            </Button>
            <Button size="sm" onClick={() => commit(draft)}>
              Save preferences
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const PrefRow: React.FC<{
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (value: boolean) => void;
}> = ({ title, description, checked, disabled, onChange }) => (
  <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-3">
    <div>
      <p className="text-sm font-semibold text-foreground">{title}</p>
      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{description}</p>
    </div>
    <Switch checked={checked} disabled={disabled} onCheckedChange={onChange} aria-label={`${title} cookies`} />
  </div>
);
